import React from "react";
import { BookOpen, Calendar, CheckSquare, BarChart, Shield, LogOut, UserCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTier } from "../../hooks/useTier";
import { useLanguage } from "../../i18n/LanguageContext";

const Sidebar = ({ activeTab, setActiveTab }) => {
  const { logout, user } = useAuth();
  const { tier } = useTier();
  const { t } = useLanguage();

  const navItems = [
    { id: "journal", icon: BookOpen, label: t("nav.journal") },
    { id: "goals", icon: CheckSquare, label: t("nav.goals") },
    { id: "calendar", icon: Calendar, label: t("nav.calendar") },
    { id: "analytics", icon: BarChart, label: t("nav.analytics") },
    ...(user?.is_admin ? [{ id: "admin", icon: Shield, label: t("nav.admin") }] : []),
  ];

  return (
    <div className="hidden md:flex w-16 md:w-64 bg-gray-50 flex-col">
      <div className="flex items-center justify-center gap-3 px-4 py-5 md:justify-start">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 60 60"
          className="h-10 w-10 flex-shrink-0"
          aria-hidden="true"
        >
          <defs>
            <linearGradient
              id="purpleGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#9B30FF" />
              <stop offset="100%" stopColor="#6A0DAD" />
            </linearGradient>
          </defs>

          <g>
            <rect
              x="10"
              y="10"
              width="40"
              height="40"
              rx="10"
              ry="10"
              fill="url(#purpleGradient)"
            />
            <line
              x1="30"
              y1="10"
              x2="30"
              y2="50"
              stroke="#FFFFFF"
              strokeWidth="1.5"
            />
            <path
              d="M20 25 Q 25 18, 30 25 Q 35 32, 40 25"
              stroke="#FFFFFF"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M20 35 Q 25 42, 30 35 Q 35 28, 40 35"
              stroke="#FFFFFF"
              strokeWidth="2"
              fill="none"
              opacity="0.7"
            />
          </g>
        </svg>
        <div className="hidden min-w-0 items-center gap-2 md:flex">
          <span className="text-xl font-semibold text-gray-900">Reflecta</span>
          <span
            className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
              tier === "premium"
                ? "bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] text-white"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            {tier === "premium" ? t("nav.premium") : t("nav.free")}
          </span>
        </div>
      </div>

      <nav className="flex-1 pt-4 px-2">
        {navItems.map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center w-full p-3 mb-1 rounded-xl transition-all duration-150 ${
              activeTab === id
                ? "bg-purple-100 text-purple-700 font-medium"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            }`}
          >
            <Icon className="h-5 w-5 mx-auto md:mx-0 md:mr-3" />
            <span className="hidden md:inline text-sm">{label}</span>
          </button>
        ))}
      </nav>

      <div className="grid grid-cols-2 gap-2 px-2 pb-4">
        <button
          onClick={() => setActiveTab("settings")}
          title={t("nav.profile")}
          aria-label={t("header.settings")}
          className={`flex items-center justify-center rounded-xl p-3 transition-all duration-150 md:justify-start ${
            activeTab === "settings"
              ? "bg-purple-100 text-purple-700 font-medium"
              : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          }`}
        >
          <UserCircle className="h-5 w-5 md:mr-2" />
          <span className="hidden md:inline text-sm">{t("nav.profile")}</span>
        </button>
        <button
          onClick={logout}
          title={t("nav.logout")}
          aria-label={t("nav.logout")}
          className="flex items-center justify-center rounded-xl p-3 text-gray-500 transition-all duration-150 hover:bg-red-50 hover:text-red-600 md:justify-start"
        >
          <LogOut className="h-5 w-5 md:mr-2" />
          <span className="hidden md:inline text-sm">{t("nav.logout")}</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
